import { CIMSPage, Permission } from 'shared'
import { useGlobalState } from 'context/GlobalStateContext'
import {
  ProjectFdcApprovalGroupsQuery,
  useCreateFdcApprovalGroupMutation,
  useEditFdcApprovalGroupMutation,
  useProjectFdcApprovalGroupsQuery,
  useRestoreFdcApprovalGroupMutation,
  useSoftRemoveFdcApprovalGroupMutation,
} from 'schema'
import {
  DefaultMutationHookOptions,
  DefaultQueryHookOptions,
} from 'utils/defaults'
import CIMSTable from 'components/CIMSTable'
import { CIMSTags } from 'components/CIMSTags'
import {
  simpleCreateNotification,
  simpleEditNotification,
  simpleRemoveNotification,
  simpleRestoreNotification,
} from 'hooks/notificationHandler'
import { CIMSColumn } from 'components/CIMSTable/CIMSColumn/types'
import { useOptionalDescriptionColumn } from 'components/CIMSTable/common/hooks/useDescriptionColumn'
import { HelpMessage } from 'utils/constants'
import ModalBody from './components/ModalBody'
import { useNameColumn } from 'components/CIMSTable/common/hooks/useNameColumn'
import { useUsersAndRolesColumn } from 'components/CIMSTable/common/hooks/useUsersAndRolesColumn'
import { useUserRoleSelectContext } from 'components/CIMSSelect/common/UserRoleSelect/UserRoleSelectContext'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { errorHandler } from '../../../../../../../hooks/errorHandler'
const Help = HelpMessage.FieldDesignChanges.ApprovalGroups

const ApprovalGroups: React.FC = () => {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const {
    data,
    loading: fetching,
    refetch,
  } = useProjectFdcApprovalGroupsQuery({
    ...DefaultQueryHookOptions,
    skip: !projectId,
    variables: {
      globalId: projectId!,
      input: { projectId: { equals: projectId } },
    },
  })
  const [create] = useCreateFdcApprovalGroupMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      createFDCApprovalGroup: {
        approvalGroup: { name },
      },
    }) => simpleCreateNotification(name),
  })
  const [remove, { loading: removing }] = useSoftRemoveFdcApprovalGroupMutation(
    {
      ...DefaultMutationHookOptions,
      onCompleted: ({
        softRemoveFDCApprovalGroup: {
          approvalGroup: { name },
        },
      }) => simpleRemoveNotification(name),
    }
  )
  const [restore, { loading: restoring }] = useRestoreFdcApprovalGroupMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      restoreFDCApprovalGroup: {
        approvalGroup: { name },
      },
    }) => simpleRestoreNotification(name),
  })
  const [edit] = useEditFdcApprovalGroupMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editFDCApprovalGroup: {
        approvalGroup: { name },
      },
    }) => simpleEditNotification(name),
  })
  const concurrenceLevels = useOptions({
    items: data?.project.fdcConcurrenceLevels,
    label: 'name',
    tooltip: 'description',
    value: 'id',
    sort: 'name',
  })
  const columns: CIMSColumn<
    ProjectFdcApprovalGroupsQuery['project']['fdcApprovalGroups'][0]
  >[] = [
    useNameColumn({ help: Help.Name, sort: { default: 'ascend' } }),
    useOptionalDescriptionColumn('Approval Group'),
    {
      title: 'Concurrence Levels',
      link: CIMSPage.FDCConcurrences,
      help: Help.ConcurrenceLevels,
      width: 200,
      render: ({ concurrenceLevels }) => (
        <CIMSTags
          tags={concurrenceLevels.map(({ name, description }) => ({
            text: name,
            tooltip: description,
          }))}
        />
      ),
      filter: {
        with: 'callback',
        items: (records) =>
          records
            .flatMap(({ concurrenceLevels }) => concurrenceLevels)
            .map(({ id, name, description }) => ({
              text: name,
              value: id,
              tooltip: description,
            })),
        onFilter: (value, { concurrenceLevels }) =>
          concurrenceLevels.some(({ id }) => id === value.toString()),
      },
    },
    useUsersAndRolesColumn({
      title: 'Approvers',
      help: Help.Approvers,
    }),
  ]
  const { toLabeledValues, fromLabeledValues } = useUserRoleSelectContext()
  return (
    <CIMSTable
      records={data?.project.fdcApprovalGroups}
      rowKey="id"
      loading={fetching || removing || restoring}
      details={`${Help.Approvers}.`}
      columns={columns}
      actions={{
        header: {
          onReload: refetch,
          create: {
            permission: Permission.CreateFDCApprovalGroup,
            entity: 'Approval Group',
            loading: fetching,
            modal: {
              body: <ModalBody concurrenceLevels={concurrenceLevels} />,
              onOk: async ({
                name,
                description,
                concurrenceLevelIds,
                rolesUsers,
              }) => {
                const { roleIds, userIds } = fromLabeledValues(rolesUsers)
                if (!projectId) return errorHandler('projectId is not defined')
                await create({
                  variables: {
                    createFdcApprovalGroupInput: {
                      name,
                      projectId,
                      description,
                      roleIds,
                      userIds,
                      concurrenceLevelIds,
                    },
                  },
                })
                refetch()
              },
            },
          },
        },
        row: {
          edit: {
            permission: Permission.EditFDCApprovalGroup,
            modal: {
              body: <ModalBody concurrenceLevels={concurrenceLevels} />,
              initialValues: ({
                concurrenceLevels,
                name,
                roles,
                users,
                description,
              }) => ({
                concurrenceLevelIds: concurrenceLevels.map(({ id }) => id),
                name,
                description,
                rolesUsers: toLabeledValues({ roles, users }),
              }),
              recordName: 'name',
              onOk: async (
                { id },
                { name, description, rolesUsers, concurrenceLevelIds }
              ) => {
                const { roleIds, userIds } = fromLabeledValues(rolesUsers)
                await edit({
                  variables: {
                    editFdcApprovalGroupInput: {
                      id,
                      concurrenceLevelIds,
                      description,
                      name,
                      roleIds,
                      userIds,
                    },
                  },
                })
                refetch()
              },
            },
          },
          remove: {
            permission: Permission.RemoveRestoreFDCApprovalGroup,
            onRemove: async ({ id }) => {
              await remove({ variables: { id } })
              refetch()
            },
            disabled: ({ fdcToFDCApprovalGroups }) =>
              !!fdcToFDCApprovalGroups.length
                ? {
                    message:
                      'Approval Groups cannot be removed if they have been approved on one or more Field Design Changes',
                  }
                : false,
          },

          restore: {
            permission: Permission.RemoveRestoreFDCApprovalGroup,
            onRestore: async ({ id }) => {
              await restore({ variables: { id } })
              refetch()
            },
            metadata: ({ deleted, deletedUser, deletedDate }) => ({
              removed: deleted,
              removedBy: deletedUser,
              removedDate: deletedDate,
            }),
            default: 'active',
          },
        },
      }}
      expand={{
        expandable: ({ fdcToFDCApprovalGroups }) =>
          !!fdcToFDCApprovalGroups.length,
        render: ({ id, fdcToFDCApprovalGroups }) => ({
          additionalInfo: [
            {
              type: 'deepLinks',
              label: 'Associated FDCs',
              help: 'Field Design Changes where this Approval Group has been approved',
              showMore: { key: id },
              links: fdcToFDCApprovalGroups.map(
                ({
                  fieldDesignChange: { deleted, formattedNumber, id: recordId },
                }) => ({
                  page: CIMSPage.FDCTracker,
                  deep: { recordId, deleted },
                  label: formattedNumber,
                })
              ),
            },
          ],
        }),
      }}
    />
  )
}

export default ApprovalGroups
